// Prescribing and the formulary (docs/plans/phase1-consultation.md §3.3 C3). Search terms
// go in POST bodies, never in the address.
import { queryOptions } from '@tanstack/react-query';
import {
  activeMedicineListSchema,
  doseRuleListSchema,
  formularyConceptSchema,
  formularyAdminListSchema,
  formularyItemAdminSchema,
  formularySearchResultSchema,
  frequencyListSchema,
  prescriptionCheckSchema,
  prescriptionListSchema,
  prescriptionPrintSchema,
  prescriptionSchema,
  type CheckPrescriptionRequest,
  type FormularyValueSet,
  type FormularyItemInput,
  type ReplaceDoseRulesRequest,
  type SignPrescriptionRequest,
  type replaceFrequenciesRequestSchema,
  type replaceGroupsRequestSchema,
  type updateFormularyItemRequestSchema,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export const prescribingKeys = {
  visit: (visitId: string) => ['visits', visitId, 'prescriptions'] as const,
  medicines: (patientId: string) => ['patients', patientId, 'medicines'] as const,
};

export function searchFormulary(visitId: string, query: string) {
  return api('POST', '/formulary/search', {
    body: { visitId, query },
    schema: formularySearchResultSchema,
  }).then((r) => r.items);
}

export const frequenciesQuery = queryOptions({
  queryKey: ['medication-frequencies'],
  queryFn: () => api('GET', '/medication-frequencies', { schema: frequencyListSchema }),
  staleTime: 60_000,
});

export function checkPrescription(visitId: string, body: CheckPrescriptionRequest) {
  return api('POST', `/visits/${visitId}/prescriptions/check`, {
    body,
    schema: prescriptionCheckSchema,
  });
}

export function signPrescription(visitId: string, body: SignPrescriptionRequest) {
  return api('POST', `/visits/${visitId}/prescriptions`, { body, schema: prescriptionSchema });
}

export const visitPrescriptionsQuery = (visitId: string) =>
  queryOptions({
    queryKey: prescribingKeys.visit(visitId),
    queryFn: () =>
      api('GET', `/visits/${visitId}/prescriptions`, { schema: prescriptionListSchema }),
  });

export const activeMedicinesQuery = (patientId: string) =>
  queryOptions({
    queryKey: prescribingKeys.medicines(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/medicines`, { schema: activeMedicineListSchema }),
  });

export type LineAction = 'stop' | 'cancel' | 'entered-in-error';

export function changeLine(
  lineId: string,
  action: LineAction,
  reason: string,
  expectedVersion: number,
) {
  return api('POST', `/prescription-lines/${lineId}/${action}`, {
    body: { reason, expectedVersion },
    schema: prescriptionSchema,
  });
}

export const prescriptionPrintQuery = (id: string) =>
  queryOptions({
    queryKey: ['prescriptions', id, 'print'],
    queryFn: () => api('GET', `/prescriptions/${id}/print`, { schema: prescriptionPrintSchema }),
  });

export function markPrescriptionPrinted(id: string) {
  return api('POST', `/prescriptions/${id}/printed`, { schema: z.object({ copy: z.number() }) });
}

// --- Settings -----------------------------------------------------------------------------------

export const formularyAdminQuery = queryOptions({
  queryKey: ['admin', 'formulary-items'],
  queryFn: () => api('GET', '/admin/formulary-items', { schema: formularyAdminListSchema }),
});

export function createFormularyItem(body: FormularyItemInput) {
  return api('POST', '/admin/formulary-items', { body, schema: formularyItemAdminSchema });
}

export function updateFormularyItem(
  id: string,
  body: z.input<typeof updateFormularyItemRequestSchema>,
) {
  return api('PUT', `/admin/formulary-items/${id}`, { body, schema: formularyItemAdminSchema });
}

export function replaceGroups(body: z.input<typeof replaceGroupsRequestSchema>) {
  return api('PUT', '/admin/formulary-groups', { body });
}

export function approveGroupMembership(id: string, expectedVersion: number) {
  return api('POST', `/admin/formulary-groups/${id}/approve`, { body: { expectedVersion } });
}

export const doseRulesQuery = queryOptions({
  queryKey: ['admin', 'dose-rules'],
  queryFn: () => api('GET', '/admin/dose-rules', { schema: doseRuleListSchema }),
});

export function replaceDoseRules(body: ReplaceDoseRulesRequest) {
  return api('PUT', '/admin/dose-rules', { body, schema: doseRuleListSchema });
}

export function approveDoseRule(id: string, expectedVersion: number) {
  return api('POST', `/admin/dose-rules/${id}/approve`, {
    body: { expectedVersion },
    schema: doseRuleListSchema,
  });
}

export function retireDoseRule(id: string, expectedVersion: number, reason: string) {
  return api('POST', `/admin/dose-rules/${id}/retire`, {
    body: { expectedVersion, reason },
    schema: doseRuleListSchema,
  });
}

export function replaceFrequencies(body: z.input<typeof replaceFrequenciesRequestSchema>) {
  return api('PUT', '/admin/medication-frequencies', { body, schema: frequencyListSchema });
}

export const formularyValueSetQuery = (valueSet: FormularyValueSet) =>
  queryOptions({
    queryKey: ['formulary-value-sets', valueSet],
    queryFn: () =>
      api('GET', `/formulary/value-sets/${valueSet}`, { schema: z.array(formularyConceptSchema) }),
    staleTime: 60_000,
  });

export function addFormularyConcept(valueSet: FormularyValueSet, display: string) {
  return api('POST', `/admin/formulary/value-sets/${valueSet}`, {
    body: { display },
    schema: formularyConceptSchema,
  });
}
